import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Alert,
  Box,
  Typography,
  Divider,
  CircularProgress,
  IconButton
} from '@mui/material';
import { Close as CloseIcon, Warning as WarningIcon } from '@mui/icons-material';
import { bookingServiceV2 } from '@/services/bookingService.v2';

interface CancellationDialogProps {
  open: boolean;
  onClose: () => void;
  bookingId: string;
  onSuccess: (suggestedRefund?: any) => void;
}

export const CancellationDialog: React.FC<CancellationDialogProps> = ({
  open,
  onClose,
  bookingId,
  onSuccess
}) => {
  const [loadingPreview, setLoadingPreview] = useState(false);
  const [cancelling, setCancelling] = useState(false);
  const [preview, setPreview] = useState<any>(null);
  const [reason, setReason] = useState('');
  const [adminNotes, setAdminNotes] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [cancelResult, setCancelResult] = useState<any>(null);
  const [showSuccessView, setShowSuccessView] = useState(false);

  // Fetch preview when dialog opens
  useEffect(() => {
    if (open && bookingId) {
      fetchPreview();
    }
  }, [open, bookingId]);

  const fetchPreview = async () => {
    try {
      setLoadingPreview(true);
      setError(null);

      const previewData = await bookingServiceV2.getCancellationPreview(bookingId);
      setPreview(previewData);

    } catch (err: any) {
      setError(err.message || 'Failed to fetch cancellation preview');
    } finally {
      setLoadingPreview(false);
    }
  };

  const handleConfirmCancel = async () => {
    if (!reason.trim()) {
      setError('Please provide a cancellation reason');
      return;
    }

    try {
      setCancelling(true);
      setError(null);

      const result = await bookingServiceV2.cancelBooking(bookingId, {
        reason: reason.trim(),
        adminNotes: adminNotes.trim() || undefined
      });

      // New V2 response includes suggestedRefund
      setCancelResult(result);
      setShowSuccessView(true);

    } catch (err: any) {
      setError(err.message || 'Failed to cancel booking');
    } finally {
      setCancelling(false);
    }
  };

  const handleFinish = () => {
    onSuccess(cancelResult?.suggestedRefund);
    handleClose();
  };

  const handleClose = () => {
    setReason('');
    setAdminNotes('');
    setError(null);
    setPreview(null);
    setCancelResult(null);
    setShowSuccessView(false);
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <WarningIcon color="error" />
            <Typography variant="h6">Cancel Booking</Typography>
          </Box>
          <IconButton size="small" onClick={handleClose}>
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent>
        {showSuccessView ? (
          /* Success view with suggested refund */
          <>
            <Alert severity="success" sx={{ mb: 3 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 0.5 }}>
                Booking Cancelled Successfully
              </Typography>
              <Typography variant="body2">
                {cancelResult?.message || 'The booking has been cancelled.'}
              </Typography>
            </Alert>

            {cancelResult?.suggestedRefund && (
              <Box sx={{ p: 2, bgcolor: 'background.default', borderRadius: 1, mb: 2 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 2 }}>
                  Suggested Refund
                </Typography>

                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                  <Typography variant="body2">Refundable Amount</Typography>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    ₦{cancelResult.suggestedRefund.refundableAmount?.toLocaleString() || 0}
                  </Typography>
                </Box>

                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                  <Typography variant="body2" color="error">
                    Penalty ({cancelResult.suggestedRefund.penaltyPercentage || 0}%)
                  </Typography>
                  <Typography variant="body2" color="error">
                    -₦{cancelResult.suggestedRefund.penaltyAmount?.toLocaleString() || 0}
                  </Typography>
                </Box>

                {cancelResult.suggestedRefund.securityDepositRefund > 0 && (
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                    <Typography variant="body2" color="success.main">Security Deposit</Typography>
                    <Typography variant="body2" color="success.main">
                      +₦{cancelResult.suggestedRefund.securityDepositRefund?.toLocaleString() || 0}
                    </Typography>
                  </Box>
                )}

                <Divider sx={{ my: 2 }} />

                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                    Total Refund
                  </Typography>
                  <Typography variant="subtitle1" sx={{ fontWeight: 700, color: 'success.main' }}>
                    ₦{cancelResult.suggestedRefund.totalRefund?.toLocaleString() || 0}
                  </Typography>
                </Box>

                {cancelResult.suggestedRefund.message && (
                  <Alert severity="info" sx={{ mt: 2 }}>
                    {cancelResult.suggestedRefund.message}
                  </Alert>
                )}
              </Box>
            )}

            {cancelResult?.nextSteps && (
              <Alert severity="info">
                <Typography variant="body2">
                  {cancelResult.nextSteps.message}
                </Typography>
              </Alert>
            )}
          </>
        ) : loadingPreview ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        ) : error ? (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        ) : preview ? (
          <>
            {/* Cannot cancel alert */}
            {!preview.canCancel && (
              <Alert severity="error" sx={{ mb: 2 }}>
                <strong>Cannot cancel this booking</strong>
                <br />
                {preview.cancellationReason || 'Booking is not eligible for cancellation'}
              </Alert>
            )}

            {/* Warnings */}
            {preview.warnings?.map((warning: string, index: number) => (
              <Alert key={index} severity="warning" sx={{ mb: 2 }}>
                {warning}
              </Alert>
            ))}

            {preview.canCancel && (
              <>
                {/* Policy info */}
                <Alert severity="info" sx={{ mb: 3 }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 0.5 }}>
                    {preview.policyApplied}
                  </Typography>
                  <Typography variant="body2">
                    Booking starts in <strong>{preview.refundCalculation?.hoursUntilStart || 0}</strong> hours
                  </Typography>
                </Alert>

                {/* Refund breakdown */}
                <Box sx={{ mb: 3, p: 2, bgcolor: 'background.default', borderRadius: 1 }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 2 }}>
                    Refund Breakdown
                  </Typography>

                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                    <Typography variant="body2">Total Paid</Typography>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                      ₦{preview.refundCalculation?.totalPaid?.toLocaleString() || 0}
                    </Typography>
                  </Box>

                  {preview.refundCalculation?.alreadyRefunded > 0 && (
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                      <Typography variant="body2" color="text.secondary">Already Refunded</Typography>
                      <Typography variant="body2" color="text.secondary">
                        -₦{preview.refundCalculation.alreadyRefunded.toLocaleString()}
                      </Typography>
                    </Box>
                  )}

                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                    <Typography variant="body2" color="error">
                      Penalty ({preview.refundCalculation?.penaltyPercentage || 0}%)
                    </Typography>
                    <Typography variant="body2" color="error">
                      -₦{preview.refundCalculation?.penaltyAmount?.toLocaleString() || 0}
                    </Typography>
                  </Box>

                  {preview.refundCalculation?.securityDepositRefund > 0 && (
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                      <Typography variant="body2" color="success.main">Security Deposit</Typography>
                      <Typography variant="body2" color="success.main">
                        +₦{preview.refundCalculation.securityDepositRefund.toLocaleString()}
                      </Typography>
                    </Box>
                  )}

                  <Divider sx={{ my: 2 }} />

                  <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                      Total Refund
                    </Typography>
                    <Typography variant="subtitle1" sx={{ fontWeight: 700, color: 'success.main' }}>
                      ₦{preview.refundCalculation?.totalRefund?.toLocaleString() || 0}
                    </Typography>
                  </Box>
                </Box>

                {/* Reason input */}
                <TextField
                  label="Cancellation Reason"
                  required
                  fullWidth
                  multiline
                  rows={3}
                  value={reason}
                  onChange={(e) => setReason(e.target.value)}
                  placeholder="e.g., Customer requested cancellation - family emergency"
                  sx={{ mb: 2 }}
                />

                {/* Admin notes */}
                <TextField
                  label="Admin Notes (Internal)"
                  fullWidth
                  multiline
                  rows={2}
                  value={adminNotes}
                  onChange={(e) => setAdminNotes(e.target.value)}
                  placeholder="Internal notes for record keeping..."
                  helperText="Optional - Only visible to admins"
                />
              </>
            )}
          </>
        ) : null}
      </DialogContent>

      <DialogActions>
        {showSuccessView ? (
          <Button onClick={handleFinish} variant="contained" color="primary">
            Done
          </Button>
        ) : (
          <>
            <Button onClick={handleClose} disabled={cancelling}>
              Cancel
            </Button>
            {preview?.canCancel && (
              <Button
                onClick={handleConfirmCancel}
                color="error"
                variant="contained"
                disabled={!reason.trim() || cancelling}
              >
                {cancelling ? 'Cancelling...' : 'Confirm Cancellation'}
              </Button>
            )}
          </>
        )}
      </DialogActions>
    </Dialog>
  );
};
